<template>
    <v-container class="pa-5" fluid>
        <StatusHistoryDialog
            :complaint-status-history="complaintStatusHistory"
            :is-open="statusHistoryDialog"
            @close-status-history-dialog="closeStatusHistoryDialog"
        />
        <ConfirmDialog
            :data="currentComplaint"
            :is-open="deleteComplaintDialog"
            :action-loading="complaintLoadingOnDelete"
            @on-confirm="deleteComplaint"
            @on-abort="closeDeleteComplaintDialog"
        >
            <template #title>
                Usuwanie reklamacji - nr <strong>{{ currentComplaint.complaintNumber }}</strong>
            </template>
            <template #content>
                Czy na pewno chcesz usunąć reklamację?
            </template>
            <template #confirmButton>
                Usuń
            </template>
        </ConfirmDialog>
        <RemoveReasonDialog
            :is-open="removeComplaintReasonDialog"
            :complaint-data="currentComplaint"
            @on-save="saveRemoveComplaintReason"
            @on-close="closeRemoveComplaintReasonDialog"
        />

        <page-title>Reklamacje</page-title>

        <v-row align="center">
            <v-col cols="12" sm="4" lg="4">
                <v-btn
                    v-if="canCreateComplaint"
                    color="secondary"
                    medium
                    data-sentry-label="Dodaj reklamację"
                    :loading="createComplaintLoading"
                    @click="createComplaint"
                >
                    <v-icon left>
                        mdi-plus
                    </v-icon>
                    Dodaj reklamację
                </v-btn>
            </v-col>
            <v-col cols="12" sm="8" lg="8">
                <v-container fluid class="pa-0">
                    <v-row justify="end" dense>
                        <v-col cols="auto">
                            <v-menu
                                v-model="filtersMenu"
                                top
                                left
                                transition="scroll-x-reverse-transition"
                                :close-on-content-click="false"
                            >
                                <template #activator="{on}">
                                    <v-btn
                                        fab
                                        :color="hasFilters ? 'secondary' : 'grey darken-1'"
                                        elevation="1"
                                        small
                                        v-on="on"
                                    >
                                        <v-icon v-if="hasFilters" color="white">
                                            mdi-filter-plus
                                        </v-icon>
                                        <v-icon v-else color="white">
                                            mdi-filter-minus
                                        </v-icon>
                                    </v-btn>
                                </template>
                                <v-card>
                                    <v-container fluid>
                                        <v-row justify="end" dense>
                                            <v-col cols="auto">
                                                <v-btn
                                                    icon
                                                    small
                                                    @click="toggleFiltersMenu()"
                                                >
                                                    <v-icon color="grey darken-2">
                                                        mdi-close
                                                    </v-icon>
                                                </v-btn>
                                            </v-col>
                                        </v-row>
                                        <v-row>
                                            <v-col cols="12" md="4" lg="3">
                                                <v-autocomplete
                                                    v-model="filters.creator"
                                                    :items="creators"
                                                    :filter="customUsersFilter"
                                                    :loading="usersSearchCreatorLoading"
                                                    :search-input.sync="usersSearchCreator"
                                                    label="Wprowadzający"
                                                    class="v-text-field--placeholder-dark"
                                                    required
                                                    solo
                                                    hide-details
                                                    item-value="id"
                                                    no-data-text="Nie znaleziono"
                                                    clearable
                                                    @keyup.enter="changeFilter"
                                                    @input="changeFilter"
                                                    @click:clear="clearFilter('creator')"
                                                >
                                                    <template slot="no-data">
                                                        <v-list-item>
                                                            <v-list-item-title>
                                                                <template v-if="!usersSearchCreator">
                                                                    Wpisz treść aby odnaleźć <strong>wprowadzającego</strong>.
                                                                </template>
                                                                <template v-else-if="usersSearchCreatorLoading">
                                                                    Trwa wyszukiwanie...
                                                                </template>
                                                                <template v-else>
                                                                    Nie znaleziono
                                                                </template>
                                                            </v-list-item-title>
                                                        </v-list-item>
                                                    </template>
                                                    <template slot="selection" slot-scope="data">
                                                        <template v-if="data.item.firstName && data.item.lastName">
                                                            {{ data.item.firstName }} {{ data.item.lastName }}
                                                        </template>
                                                        <template v-else>
                                                            {{ data.item.username }}
                                                        </template>
                                                    </template>
                                                    <template slot="item" slot-scope="data">
                                                        <template v-if="data.item.firstName && data.item.lastName">
                                                            {{ data.item.firstName }} {{ data.item.lastName }}
                                                        </template>
                                                        <template v-else>
                                                            {{ data.item.username }}
                                                        </template>
                                                    </template>
                                                </v-autocomplete>
                                            </v-col>
                                            <v-col cols="12" md="4" lg="3">
                                                <v-autocomplete
                                                    v-model="filters.assigned"
                                                    :items="assigned"
                                                    :filter="customUsersFilter"
                                                    label="Rozpatrujący"
                                                    class="v-text-field--placeholder-dark"
                                                    required
                                                    solo
                                                    hide-details
                                                    item-value="id"
                                                    no-data-text="Nie znaleziono"
                                                    clearable
                                                    @keyup.enter="changeFilter"
                                                    @input="changeFilter"
                                                    @click:clear="clearFilter('assigned')"
                                                >
                                                    <template slot="selection" slot-scope="data">
                                                        <template v-if="data.item.firstName && data.item.lastName">
                                                            {{ data.item.firstName }} {{ data.item.lastName }}
                                                        </template>
                                                        <template v-else>
                                                            {{ data.item.username }}
                                                        </template>
                                                    </template>
                                                    <template slot="item" slot-scope="data">
                                                        <template v-if="data.item.firstName && data.item.lastName">
                                                            {{ data.item.firstName }} {{ data.item.lastName }}
                                                        </template>
                                                        <template v-else>
                                                            {{ data.item.username }}
                                                        </template>
                                                    </template>
                                                </v-autocomplete>
                                            </v-col>
                                            <v-col cols="12" md="8" lg="6">
                                                <v-autocomplete
                                                    v-model="filters.responsiblePerson_ids"
                                                    :items="responsiblePerson"
                                                    :filter="customUsersFilter"
                                                    :loading="usersSearchResponsiblePersonLoading"
                                                    :search-input.sync="usersSearchResponsiblePerson"
                                                    label="Osoby odpowiedzialne"
                                                    class="v-text-field--placeholder-dark"
                                                    required
                                                    chips
                                                    deletable-chips
                                                    hide-details
                                                    cache-items
                                                    multiple
                                                    solo
                                                    item-value="id"
                                                    no-data-text="Nie znaleziono"
                                                    clearable
                                                    @change="changeFilter"
                                                >
                                                    <template slot="no-data">
                                                        <v-list-item>
                                                            <v-list-item-title>
                                                                <template v-if="!usersSearchResponsiblePerson">
                                                                    Wpisz treść aby odnaleźć <strong>osobę odpowiedzialną</strong>.
                                                                </template>
                                                                <template v-else-if="usersSearchResponsiblePersonLoading">
                                                                    Trwa wyszukiwanie...
                                                                </template>
                                                                <template v-else>
                                                                    Nie znaleziono
                                                                </template>
                                                            </v-list-item-title>
                                                        </v-list-item>
                                                    </template>
                                                    <template #selection="data">
                                                        <template v-if="data.item.user">
                                                            <template v-if="data.item.user.firstName && data.item.user.lastName">
                                                                <v-chip
                                                                    v-if="data.item.confirm"
                                                                    color="success"
                                                                    close
                                                                    @update:active="removeUser(data.item)"
                                                                >
                                                                    {{ data.item.user.firstName }} {{ data.item.user.lastName }}
                                                                </v-chip>
                                                                <v-chip
                                                                    v-else
                                                                    close
                                                                    @update:active="removeUser(data.item)"
                                                                >
                                                                    {{ data.item.user.firstName }} {{ data.item.user.lastName }}
                                                                </v-chip>
                                                            </template>
                                                            <template v-else>
                                                                <v-chip
                                                                    v-if="data.item.confirm"
                                                                    close
                                                                    color="success"
                                                                    @update:active="removeUser(data.item)"
                                                                >
                                                                    {{ data.item.user.username }}
                                                                </v-chip>
                                                                <v-chip
                                                                    v-else
                                                                    close
                                                                    @update:active="removeUser(data.item)"
                                                                >
                                                                    {{ data.item.user.username }}
                                                                </v-chip>
                                                            </template>
                                                        </template>
                                                        <template v-else>
                                                            <template v-if="data.item.firstName && data.item.lastName">
                                                                <v-chip
                                                                    close
                                                                    @update:active="removeUser(data.item)"
                                                                >
                                                                    {{ data.item.firstName }} {{ data.item.lastName }}
                                                                </v-chip>
                                                            </template>
                                                            <template v-else>
                                                                <v-chip
                                                                    close
                                                                    @update:active="removeUser(data.item)"
                                                                >
                                                                    {{ data.item.username }}
                                                                </v-chip>
                                                            </template>
                                                        </template>
                                                    </template>

                                                    <template #item="data">
                                                        <template v-if="data.item.user">
                                                            <template v-if="data.item.user.firstName && data.item.user.lastName">
                                                                {{ data.item.user.firstName }} {{ data.item.user.lastName }}
                                                            </template>
                                                            <template v-else>
                                                                {{ data.item.user.username }}
                                                            </template>
                                                        </template>
                                                        <template v-else>
                                                            <template v-if="data.item.firstName && data.item.lastName">
                                                                {{ data.item.firstName }} {{ data.item.lastName }}
                                                            </template>
                                                            <template v-else>
                                                                {{ data.item.username }}
                                                            </template>
                                                        </template>
                                                    </template>
                                                </v-autocomplete>
                                            </v-col>
                                        </v-row>
                                        <v-row>
                                            <v-col cols="12" md="6" lg="4">
                                                <v-select
                                                    v-model="filters.complaintStatus"
                                                    :items="complaintStatuses"
                                                    label="Wybierz statusy..."
                                                    item-value="id"
                                                    item-text="name"
                                                    chips
                                                    clearable
                                                    multiple
                                                    hide-details
                                                    solo
                                                    @change="changeFilter"
                                                >
                                                    <template #selection="data">
                                                        <v-chip
                                                            close
                                                            @update:active="removeStatus(data.item)"
                                                        >
                                                            {{ data.item.name }}
                                                        </v-chip>
                                                    </template>
                                                </v-select>
                                            </v-col>
                                            <v-col cols="12" md="6" lg="2">
                                                <v-select
                                                    v-model="filters.complaintProductionDepartment"
                                                    :items="complaintProductionDepartments"
                                                    item-value="id"
                                                    label="Wybierz dział..."
                                                    no-data-text="Brak danych..."
                                                    clearable
                                                    hide-details
                                                    single-line
                                                    solo
                                                    @change="changeFilter"
                                                >
                                                    <template slot="selection" slot-scope="data">
                                                        {{ data.item.name }}
                                                    </template>

                                                    <template slot="item" slot-scope="data">
                                                        {{ data.item.name }}
                                                    </template>
                                                </v-select>
                                            </v-col>
                                            <v-col cols="12" md="6" lg="2">
                                                <v-select
                                                    v-model="filters.complaintType_id"
                                                    :items="complaintTypes"
                                                    item-value="id"
                                                    label="Wybierz rodzaj..."
                                                    no-data-text="Brak danych..."
                                                    clearable
                                                    hide-details
                                                    single-line
                                                    solo
                                                    @change="changeFilter"
                                                >
                                                    <template slot="selection" slot-scope="data">
                                                        {{ data.item.name }}
                                                    </template>

                                                    <template slot="item" slot-scope="data">
                                                        {{ data.item.name }}
                                                    </template>
                                                </v-select>
                                            </v-col>
                                            <v-col cols="12" md="4" lg="2">
                                                <v-menu
                                                    v-model="menuFrom"
                                                    :close-on-content-click="false"
                                                    :nudge-right="40"
                                                    transition="scale-transition"
                                                    offset-y
                                                    min-width="290px"
                                                >
                                                    <template #activator="{on}">
                                                        <v-text-field
                                                            v-model="filters.createdAt__gte"
                                                            label="Od:"
                                                            prepend-icon="mdi-calendar"
                                                            readonly
                                                            clearable
                                                            solo
                                                            hide-details
                                                            v-on="on"
                                                            @input="changeFilter"
                                                            @click:clear="clearFilter('createdAt__gte')"
                                                        />
                                                    </template>
                                                    <v-date-picker
                                                        v-model="filters.createdAt__gte"
                                                        first-day-of-week="1"
                                                        @change="changeFilter"
                                                        @input="menuFrom = false"
                                                    />
                                                </v-menu>
                                            </v-col>
                                            <v-col cols="12" md="4" lg="2">
                                                <v-menu
                                                    v-model="menuTo"
                                                    :close-on-content-click="false"
                                                    :nudge-right="40"
                                                    transition="scale-transition"
                                                    offset-y
                                                    min-width="290px"
                                                >
                                                    <template #activator="{on}">
                                                        <v-text-field
                                                            v-model="filters.createdAt__lte"
                                                            label="Do:"
                                                            prepend-icon="mdi-calendar"
                                                            readonly
                                                            clearable
                                                            solo
                                                            hide-details
                                                            v-on="on"
                                                            @input="changeFilter"
                                                            @click:clear="clearFilter('createdAt__lte')"
                                                        />
                                                    </template>
                                                    <v-date-picker
                                                        v-model="filters.createdAt__lte"
                                                        first-day-of-week="1"
                                                        @change="changeFilter"
                                                        @input="menuTo = false"
                                                    />
                                                </v-menu>
                                            </v-col>
                                        </v-row>
                                        <v-row justify="end">
                                            <v-col cols="12" md="6" lg="6">
                                                <v-text-field
                                                    v-model="filters.search"
                                                    append-icon="mdi-magnify"
                                                    label="Szukaj..."
                                                    single-line
                                                    clearable
                                                    hide-details
                                                    solo
                                                    @keyup.enter="changeFilter"
                                                    @click:append="changeFilter"
                                                    @click:clear="clearFilter('search')"
                                                />
                                            </v-col>
                                        </v-row>
                                    </v-container>
                                </v-card>
                            </v-menu>
                        </v-col>
                    </v-row>
                </v-container>
            </v-col>
        </v-row>
        <v-row justify="end" no-gutters>
            <v-col cols="auto">
                <v-btn
                    text
                    x-small
                    data-sentry-label="Generuj raport"
                    :disabled="generateReportLoading"
                    @click="generateReport"
                >
                    <v-icon
                        v-if="!generateReportLoading"
                        small
                        color="green"
                    >
                        mdi-file-excel
                    </v-icon>
                    <v-progress-circular
                        v-else
                        indeterminate
                        color="primary"
                        class="mr-1"
                        size="12"
                        width="2"
                    />
                    Generuj raport
                </v-btn>
            </v-col>
        </v-row>
        <v-row>
            <v-col cols="12">
                <v-card>
                    <v-data-table
                        :headers="complaintsHeaders"
                        :items="complaints"
                        :loading="complaintsLoading"
                        :page.sync="pagination.page"
                        :footer-props.sync="footerProps"
                        :server-items-length="pagination.itemsLength"
                        :items-per-page.sync="pagination.itemsPerPage"
                        :mobile-breakpoint="0"
                        name="complaintsData"
                        @pagination="updatePagination"
                    >
                        <template #item="{item}">
                            <tr :class="{'isSelected': selectedComplaintId == item.id}">
                                <td :class="complaintLabelClass(item.complaintStatusId)">
                                    <v-tooltip v-if="item.priority" :open-delay="tooltipDelay" top>
                                        <template #activator="{on}">
                                            <span
                                                icon
                                                v-on="on"
                                            >
                                                <v-icon color="pink">
                                                    mdi-priority-high
                                                </v-icon>
                                            </span>
                                        </template>
                                        <span>Priorytet</span>
                                    </v-tooltip>
                                </td>
                                <td>
                                    {{ item.complaintNumber }}
                                </td>
                                <td>
                                    {{ getOrderNumberLabels(item)[0] }}
                                    <v-chip
                                        v-if="getOrderNumberLabels(item).length > 1"
                                        class="ma-1"
                                        small
                                        color="warning"
                                    >
                                        +1
                                    </v-chip>
                                </td>
                                <td>
                                    {{ item.complaintStatus.name }}
                                    <v-tooltip v-if="item.isExpired" :open-delay="tooltipDelay" top>
                                        <template #activator="{on}">
                                            <v-icon color="warning" small v-on="on">
                                                mdi-clock-outline
                                            </v-icon>
                                        </template>
                                        <span>Opóźnione</span>
                                    </v-tooltip>
                                </td>
                                <td>
                                    <template v-if="item.complaintProductionDepartment">
                                        {{ item.complaintProductionDepartment.name }}
                                    </template>
                                    <template v-else>
                                        -
                                    </template>
                                </td>
                                <td>
                                    {{ formatDate(item.createdAt) }}
                                </td>
                                <td>
                                    {{ formatDate(item.modifiedAt) }}
                                </td>
                                <td>
                                    <template v-if="item.creator.firstName && item.creator.lastName">
                                        {{ item.creator.firstName }} {{ item.creator.lastName }}
                                    </template>
                                    <template v-else>
                                        {{ item.creator.username }}
                                    </template>
                                </td>
                                <td>
                                    <template v-if="item.assigned">
                                        <template v-if="item.assigned.firstName && item.assigned.lastName">
                                            {{ item.assigned.firstName }} {{ item.assigned.lastName }}
                                        </template>
                                        <template v-else>
                                            {{ item.assigned.username }}
                                        </template>
                                    </template>
                                    <template v-else>
                                        -
                                    </template>
                                </td>
                                <td>
                                    <div class="d-inline-flex">
                                        <v-tooltip :open-delay="tooltipDelay" top>
                                            <template #activator="{on}">
                                                <v-btn
                                                    icon
                                                    data-sentry-label="Historia"
                                                    v-on="on"
                                                    @click="getComplaintStatusHistory(item.id)"
                                                >
                                                    <v-icon color="blue-grey">
                                                        mdi-history
                                                    </v-icon>
                                                </v-btn>
                                            </template>
                                            <span>Historia</span>
                                        </v-tooltip>
                                        <v-tooltip :open-delay="tooltipDelay" top>
                                            <template #activator="{on}">
                                                <v-btn
                                                    icon
                                                    data-sentry-label="Edytuj"
                                                    v-on="on"
                                                    @click="editComplaint(item.id)"
                                                >
                                                    <v-icon color="teal">
                                                        mdi-pencil
                                                    </v-icon>
                                                </v-btn>
                                            </template>
                                            <span>Edytuj</span>
                                        </v-tooltip>
                                        <v-tooltip v-if="hasDKJDepartment || (isComplaintCreator(item) && isDraftComplaint(item)) || (canDeleteComplaint && (isDraftComplaint(item) || isNewComplaint(item)))" :open-delay="tooltipDelay" top>
                                            <template #activator="{on}">
                                                <v-btn
                                                    icon
                                                    data-sentry-label="Usuń"
                                                    v-on="on"
                                                    @click="showDeleteComplaintDialog(item)"
                                                >
                                                    <v-icon color="pink">
                                                        mdi-delete
                                                    </v-icon>
                                                </v-btn>
                                            </template>
                                            <span>Usuń</span>
                                        </v-tooltip>
                                    </div>
                                </td>
                            </tr>
                        </template>
                    </v-data-table>
                </v-card>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import router from '@/router'
import PageTitle from '@/components/common/PageTitle'
import ConfirmDialog from '@/components/common/ConfirmDialog'
import StatusHistoryDialog from './StatusHistoryDialog'
import RemoveReasonDialog from './RemoveReasonDialog'
import filters from '@/mixins/dataTableFilters'
import pagination from '@/mixins/dataTablePagination'
import customFilters from '@/mixins/customFilters'
import dateFormatter from '@/mixins/dateFormatter'
import tooltipDelay from '@/mixins/tooltip'
import { errorHandler, addSentryBreadcrumb } from '@/utils/errorHandler'
import { hasUserPermission } from '@/utils/permissionsHandler'
import dataTableClassSetter from '@/mixins/dataTableClassSetter'

let searchTimer

export default {
    name: 'ComplaintsList',
    components: {
        PageTitle, ConfirmDialog, RemoveReasonDialog, StatusHistoryDialog
    },
    mixins: [
        dateFormatter,
        tooltipDelay,
        filters,
        customFilters,
        pagination,
        dataTableClassSetter
    ],
    beforeRouteLeave(to, from, next) {
        if (!this.filtersCleared) {
            this.$store.dispatch('complaints/clearComplaintsFilters')
            this.clearSelectedListItemId()
        }
        this.resetFiltersCleared()
        next()
    },
    data() {
        return {
            filtersMenu: false,
            usersSearchCreator: null,
            usersSearchCreatorLoading: false,
            createComplaintLoading: false,
            creators: [],
            assigned: [],
            usersSearchResponsiblePerson: null,
            usersSearchResponsiblePersonLoading: false,
            responsiblePerson: [],
            currentComplaint: {},
            menuFrom: false,
            menuTo: false,
            user: {
                profile: {
                    productionDepartment: {
                        name: null
                    }
                }
            },
            complaintStatusHistory: [],
            complaintLoading: false,
            generateReportLoading: false,
            statusHistoryDialog: false,
            complaintLoadingOnDelete: false,
            deleteComplaintDialog: false,
            removeComplaintReasonDialog: false,
            hasFilters: false,
            complaintsHeaders: [
                {
                    value: 'priority',
                    sortable: false,
                },
                {
                    text: 'Numer reklamacji',
                    value: 'complaintNumber',
                    sortable: false,
                    width: 160
                },
                {
                    text: 'Numer zamówienia',
                    value: 'orderNumbers',
                    sortable: false,
                    width: 330
                },
                {
                    text: 'Status',
                    value: 'complaintStatus',
                    sortable: false,
                },
                {
                    text: 'Dział',
                    value: 'complaintDepartment',
                    sortable: false,
                },
                {
                    text: 'Data złożenia',
                    value: 'createdAt',
                    sortable: false,
                },
                {
                    text: 'Data ostatniej modyfikacji',
                    value: 'modifiedAt',
                    sortable: false,
                },
                {
                    text: 'Zgłaszający',
                    value: 'creator',
                    sortable: false,
                },
                {
                    text: 'Rozpatrujący',
                    value: 'assigned',
                    sortable: false,
                },
                {
                    text: 'Akcje',
                    value: 'actions',
                    sortable: false,
                    align: 'center',
                    width: 100
                }
            ],
        }
    },
    computed: {
        userId() {
            return this.$store.state.user.userId
        },
        complaints() {
            return this.$store.state.complaints.complaints.complaints
        },
        complaintsLoading() {
            return this.$store.state.complaints.complaints.complaintsLoading
        },
        complaintStatuses() {
            return this.$store.state.complaints.complaints.complaintStatuses
        },
        complaintTypes() {
            return this.$store.state.complaints.complaints.complaintTypes
        },
        complaintProductionDepartments() {
            return this.$store.state.administration.users.productionDepartments
        },
        savedFilters() {
            return this.$store.state.complaints.complaints.filters
        },
        selectedComplaintId() {
            return this.$store.state.common.selectedListItemId
        },
        canCreateComplaint() {
            return hasUserPermission('complaints_create')
        },
        canDeleteComplaint() {
            return hasUserPermission('complaints_delete') || hasUserPermission('comlaints_draft_delete')
        },
        filterQuery() {
            const filters = Object.assign({}, this.filters)
            let query = ''

            for (let item in filters) {
                if (filters[item] != null && filters[item] != '') {
                    if (item == 'createdAt__gte') {
                        filters[item] += 'T00:00:00Z'
                    }

                    if (item == 'createdAt__lte') {
                        filters[item] += 'T23:59:59Z'
                    }
                    query = query.concat('&', `${item}=${filters[item]}`)
                }
            }

            return query
        },
        hasDKJDepartment() {
            return this.user.profile.productionDepartment.name === 'DKJ'
        },
    },
    watch: {
        usersSearchCreator(value = null) {
            if (value === null) return

            this.usersSearchCreatorLoading = true

            clearTimeout(searchTimer)

            searchTimer = setTimeout(() => {
                if (value) {
                    this.searchUsersCreator(value)
                } else {
                    this.usersSearchCreatorLoading = false
                    this.creators = []
                }
            }, 500)
        },
        usersSearchResponsiblePerson(value = null) {
            if (value === null) return

            this.usersSearchResponsiblePersonLoading = true

            clearTimeout(searchTimer)

            searchTimer = setTimeout(() => {
                if (value) {
                    this.searchResponsiblePerson(value)
                } else {
                    this.usersSearchResponsiblePersonLoading = false
                    this.responsiblePerson = []
                }
            }, 500)
        },
    },
    beforeMount() {
        if (!this.savedPagination.pagination) {
            this.resetPagination()
        }
    },
    mounted() {
        this.$store.dispatch('administration/getUser', this.userId )
            .then(response => {
                this.user = response.data
                this.responsiblePerson.push(response.data)
                this.getComplaints()
            })
            .catch(error => {
                errorHandler(error)
            })
        Promise.all([
            this.$store.dispatch('complaints/getComplaintStatuses'),
            this.$store.dispatch('complaints/getComplaintTypes'),
            this.$store.dispatch('administration/getProductionDepartments'),
        ])
            .then((response) => {
                this.$store.dispatch('administration/getUsers', {
                    query: `?productionDepartment_id=${response[2].data.results.find(item => item.name === 'DKJ').id}`
                })
                    .then((response) => {
                        this.assigned = response.data.results
                    })
                    .catch(error => {
                        errorHandler(error)
                    })
            })
            .catch(error => {
                errorHandler(error)
            })
    },
    created() {
        this.checkFilters()
    },
    methods: {
        isComplaintCreator(complaint) {
            return complaint.creator.id === this.userId
        },
        isDraftComplaint(complaint) {
            return complaint.complaintStatusId === 15 // draft
        },
        isNewComplaint(complaint) {
            return complaint.complaintStatusId === 1 // nowa
        },
        toggleFiltersMenu() {
            this.filtersMenu = !this.filtersMenu
        },
        getOrderNumberLabels(item) {
            if (item.orderNumber) {
                return item.orderNumber.split(',')
            }

            return ['-']
        },
        searchUsersCreator(query = '') {
            addSentryBreadcrumb('ui.action', 'Wyszukiwanie użytkownika')

            this.$store.dispatch('administration/findUsers', {
                paginate: '?limit=20',
                query: query
            })
                .then(response => {
                    this.creators = response.data.results
                })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.usersSearchCreatorLoading = false
                })
        },
        searchResponsiblePerson(query = '') {
            addSentryBreadcrumb('ui.action', 'Wyszukiwanie użytkownika')

            this.$store.dispatch('administration/findUsers', {
                paginate: '?limit=20',
                query: query
            })
                .then(response => {
                    this.responsiblePerson = response.data.results
                })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.usersSearchResponsiblePersonLoading = false
                })
        },
        removeStatus(item) {
            this.filters.complaintStatus.splice(this.filters.complaintStatus.indexOf(item), 1)
            this.checkFilters()
        },
        removeUser(item) {
            this.filters.responsiblePerson_ids.splice(this.filters.responsiblePerson_ids.indexOf(item), 1)
            this.checkFilters()
            this.changeFilter()
        },
        getComplaints() {
            this.$store.dispatch('complaints/getComplaints', {
                paginate: this.paginate(),
                query: this.filterQuery
            })
                .then(response => {
                    this.handlePaginationResponse(response)
                })
                .catch(error => {
                    errorHandler(error)
                })
        },
        getComplaintStatusHistory(id) {
            this.$store.dispatch('complaints/getComplaintStatusHistory', id)
                .then(response => {
                    this.complaintStatusHistory = response.data.results
                    this.statusHistoryDialog = true
                }).catch(error => {
                    errorHandler(error)
                })
        },
        createComplaint() {
            router.push({ name: 'complaintsComplaintsCreate' })
                .catch(error => {
                    errorHandler(error)
                })
        },
        editComplaint(complaintId) {
            this.savePagination(this.$options.name)
            this.saveFiltersCleared()
            this.setSelectedListItemId(complaintId)
            router.push({ name: 'complaintsComplaintsEdit', params: { id: complaintId } })
                .catch(error => {
                    errorHandler(error)
                })
        },
        complaintLabelClass(value) {
            switch (value) {
                case 1:
                    return 'v-data-table__label v-data-table__label--danger'
                case 3:
                    return 'v-data-table__label v-data-table__label--warning'
                default:
                    break
            }
        },
        closeStatusHistoryDialog() {
            this.statusHistoryDialog = false
        },
        deleteComplaint(complaint) {
            this.complaintLoadingOnDelete = true

            if (this.hasDKJDepartment || this.isDraftComplaint(complaint)) {
                this.$store.dispatch('complaints/deleteComplaint', complaint.id)
                    .then(() => {
                        this.getComplaints()
                    })
                    .catch(error => {
                        errorHandler(error)
                    })
                    .finally(() => {
                        this.closeDeleteComplaintDialog()
                        this.complaintLoadingOnDelete = false
                    })
            } else if (this.isNewComplaint(complaint)) {
                this.complaintLoadingOnDelete = false
                this.removeComplaintReasonDialog = true
                this.closeDeleteComplaintDialog()
            }
        },
        saveRemoveComplaintReason({id, complaintRemoveReasonId}) {
            this.complaintLoadingOnDelete = true
            this.$store.dispatch('complaints/getComplaint', id)
                .then(({data}) => {
                    data.complaintRemoveReasonId = complaintRemoveReasonId

                    return this.$store.dispatch('complaints/updateComplaint', data)
                })
                .then(() => {
                    return this.$store.dispatch('complaints/deleteComplaint', id)
                })
                .then(() => {
                    this.getComplaints()
                })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.closeDeleteComplaintDialog()
                    this.complaintLoadingOnDelete = false
                })

        },
        showDeleteComplaintDialog(complaint) {
            this.currentComplaint = complaint
            this.deleteComplaintDialog = true
        },
        showRemoveComplaintReasonDialog(complaint) {
            this.currentComplaint = complaint
            this.removeComplaintReasonDialog = true
        },
        closeDeleteComplaintDialog() {
            if (!this.removeComplaintReasonDialog) {
                this.currentComplaint = {}
            }
            this.deleteComplaintDialog = false
        },
        closeRemoveComplaintReasonDialog() {
            this.currentComplaint = {}
            this.removeComplaintReasonDialog = false
        },
        checkFilters() {
            this.hasFilters = !Object.values(this.filters).every(item => item == null || item.length == 0)
        },
        changeFilter() {
            this.$store.dispatch('complaints/setComplaintsFilters', this.filters)
            this.checkFilters()
            this.resetPagination()
            this.getComplaints()

            if (this.usersSearchResponsiblePerson) {
                this.usersSearchResponsiblePerson = null
            }
        },
        generateReport() {
            this.generateReportLoading = true
            this.$store.dispatch('complaints/generateReport', {
                query: this.filterQuery,
                userEmail: this.user.email
            })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.generateReportLoading = false
                })
        },
        updatePagination(pagination) {
            this.pagination = pagination
            this.evaluateSavedPagination(this.$options.name)
            if (this.pagination.pageCount !== 0 && !this.paginationSettings.paginationPending && !this.savedPagination.pagination) {
                this.getComplaints()
            }
            this.clearPaginationPending()
        }
    }
}
</script>